<template>
  <div>
    <!-- Toolbar -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Kind</label>
            <b-form-select
              v-model="filter.kind"
              :options="kindOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Market</label>
            <b-form-select
              v-model="filter.market"
              :options="marketOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Sembol</label>
            <b-form-input
              v-model="filter.symbol"
              placeholder="BTCUSDT"
              @input="onSearchInput"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">TF</label>
            <b-form-select
              v-model="filter.timeframe"
              :options="tfOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Durum</label>
            <b-form-select
              v-model="filter.isActive"
              :options="activeOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="6"
            md="2"
            class="d-flex align-items-end mb-1"
          >
            <b-button
              variant="warning"
              block
              @click="openScanModal"
            >
              <feather-icon icon="ZapIcon" /> Yeni Scan
            </b-button>
          </b-col>
          <b-col
            cols="6"
            md="2"
            class="d-flex align-items-end mb-1"
          >
            <b-button
              variant="outline-info"
              block
              :disabled="rescanning"
              title="Mevcut edge skorlarına göre top-N aktif map'i tazele"
              @click="onRescanNow"
            >
              <b-spinner
                v-if="rescanning"
                small
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
              /> Rescan Şimdi
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Bulk actions -->
    <b-card
      v-if="selectedIds.length > 0"
      no-body
      class="mb-2 bg-light-warning"
    >
      <b-card-body class="d-flex align-items-center py-1">
        <span class="text-warning font-weight-bold">{{ selectedIds.length }} satır seçili.</span>
        <b-button
          variant="outline-success"
          size="sm"
          class="ml-2"
          @click="bulkAction(true)"
        >
          <feather-icon icon="ZapIcon" /> Aktif Yap
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="ml-1"
          @click="bulkAction(false)"
        >
          <feather-icon icon="PowerIcon" /> Pasif Yap
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          class="ml-1"
          @click="bulkRemove"
        >
          <feather-icon icon="Trash2Icon" /> Sil
        </b-button>
        <b-button
          variant="link"
          size="sm"
          class="ml-auto"
          @click="selectedIds = []"
        >
          Seçimi Temizle
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Edge table -->
    <b-card no-body>
      <b-card-body class="d-flex align-items-center py-1">
        <h4 class="mb-0">
          Edge Library ({{ metaData.total }})
        </h4>
      </b-card-body>
      <b-table
        responsive="sm"
        :fields="fields"
        :items="dataList"
        :busy="loading"
        striped
        show-empty
        empty-text="Edge bulunamadı. 'Yeni Scan' ile coin × TF grid'inde backtest çalıştır."
      >
        <template #head(select)>
          <b-form-checkbox
            :checked="allSelected"
            @change="toggleAllSelected"
          />
        </template>
        <template #cell(select)="data">
          <b-form-checkbox
            :checked="selectedIds.includes(data.item.id)"
            @change="toggleSelected(data.item.id)"
          />
        </template>
        <template #cell(symbol)="data">
          <div class="font-weight-bold">
            {{ data.item.symbol }}
          </div>
          <div class="font-small-3 text-muted">
            {{ data.item.market }} · {{ data.item.timeframe }}
          </div>
        </template>
        <template #cell(indicator)="data">
          <div
            v-if="data.item.indicator"
            class="font-small-3"
          >
            {{ data.item.indicator.name }}
          </div>
          <div
            v-if="data.item.indicator"
            class="font-small-2 text-muted"
          >
            {{ data.item.indicator.engineKey }}
          </div>
        </template>
        <template #cell(kind)="data">
          <b-badge :variant="data.item.kind === 'QUICK_GAIN' ? 'warning' : 'info'">
            {{ data.item.kind === 'QUICK_GAIN' ? 'Quick Gain' : 'Normal' }}
          </b-badge>
        </template>
        <template #cell(metrics)="data">
          <div class="font-small-3">
            WR: <strong :class="(data.item.winRate || 0) >= 50 ? 'text-success' : ''">{{ formatNum(data.item.winRate, 1) }}%</strong>
          </div>
          <div class="font-small-3">
            EV/trade:
            <strong :class="(data.item.netEvPerTrade || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(data.item.netEvPerTrade, 3) }}%
            </strong>
          </div>
          <div class="font-small-2 text-muted">
            {{ data.item.exits }} trade · DD {{ formatNum(data.item.maxDrawdown, 2) }}%
          </div>
        </template>
        <template #cell(qg)="data">
          <div
            v-if="data.item.kind === 'QUICK_GAIN'"
            class="font-small-3"
          >
            TP {{ formatNum(data.item.qgTp, 2) }}% / SL {{ formatNum(data.item.qgSl, 2) }}%
          </div>
          <div
            v-if="data.item.kind === 'QUICK_GAIN'"
            class="font-small-2 text-muted"
          >
            Lev {{ formatNum(data.item.qgLeverage, 0) }}x
          </div>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>
        <template #cell(score)="data">
          <div class="font-weight-bold">
            {{ formatNum(data.item.scoreFinal, 3) }}
          </div>
          <div class="font-small-2 text-muted">
            base {{ formatNum(data.item.scoreBase, 3) }}
          </div>
        </template>
        <template #cell(isActive)="data">
          <b-form-checkbox
            :checked="data.item.isActive"
            switch
            @change="onToggleActive(data.item.id, $event)"
          />
        </template>
        <template #cell(lastRescannedAt)="data">
          <div class="font-small-3 text-muted">
            {{ formatDateTime(data.item.lastRescannedAt) }}
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="outline-danger"
            size="sm"
            @click="onRemove(data.item.id)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-card-footer>
        <b-row align-v="center">
          <b-col
            cols="12"
            md="6"
          >
            <small class="text-muted">
              Toplam {{ metaData.total }} edge (Sayfa {{ metaData.page }} / {{ metaData.totalPages || 1 }})
            </small>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-right"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="metaData.total"
              :per-page="filter.limit"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0"
              @input="onPageChange"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </b-card-footer>
    </b-card>

    <!-- ─── Scan modal ──────────────────────────────────────────────────────── -->
    <b-modal
      v-model="scanModal.show"
      title="Edge Scan — Backtest Grid"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Coin'ler (virgülle)</label>
          <b-form-input
            v-model="scanForm.coinsRaw"
            placeholder="BTCUSDT,ETHUSDT,SOLUSDT"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Timeframe'ler (virgülle)</label>
          <b-form-input
            v-model="scanForm.tfsRaw"
            placeholder="5m,15m,1h"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">İndikatörler</label>
          <b-form-select
            v-model="scanForm.indicatorIds"
            :options="indicatorOptions"
            multiple
            :select-size="5"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Market</label>
          <b-form-select
            v-model="scanForm.market"
            :options="marketOptions.slice(1)"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Edge Tipi</label>
          <b-form-select
            v-model="scanForm.kind"
            :options="kindOptions.slice(1)"
          />
        </b-col>

        <!-- Quick Gain specifics -->
        <template v-if="scanForm.kind === 'QUICK_GAIN'">
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">TP %</label>
            <b-form-input
              v-model.number="scanForm.tpPerc"
              type="number"
              step="0.1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">SL %</label>
            <b-form-input
              v-model.number="scanForm.slPerc"
              type="number"
              step="0.1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Kaldıraç</label>
            <b-form-input
              v-model.number="scanForm.leverage"
              type="number"
              step="1"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Komisyon/Yön %</label>
            <b-form-input
              v-model.number="scanForm.commissionPerSide"
              type="number"
              step="0.01"
            />
          </b-col>
        </template>

        <!-- Filters -->
        <b-col cols="12">
          <hr><h6>Threshold Filtreleri (eşiği geçenler kayıt edilir)</h6>
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Min Trade</label>
          <b-form-input
            v-model.number="scanForm.minTrades"
            type="number"
            step="1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Min Net EV %</label>
          <b-form-input
            v-model.number="scanForm.minNetEv"
            type="number"
            step="0.01"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Min Win Rate %</label>
          <b-form-input
            v-model.number="scanForm.minWinRate"
            type="number"
            step="1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Mum sayısı</label>
          <b-form-input
            v-model.number="scanForm.candleLimit"
            type="number"
            step="100"
            min="100"
            max="1500"
          />
        </b-col>
      </b-row>

      <div class="text-right mb-2">
        <small class="text-muted mr-2">
          Tahmini iş: {{ gridSize }} kombinasyon
        </small>
        <b-button
          variant="warning"
          :disabled="scanLoading || gridSize === 0"
          @click="runScan"
        >
          <feather-icon icon="PlayIcon" /> Scan Çalıştır
        </b-button>
      </div>

      <div
        v-if="scanLoading"
        class="text-center py-3"
      >
        <b-spinner /> Scan çalışıyor... ({{ gridSize }} kombinasyon)
      </div>

      <!-- Scan results -->
      <div v-if="scanResult && !scanLoading">
        <hr>
        <b-row class="mb-2">
          <b-col
            cols="6"
            md="3"
          >
            <strong>Toplam:</strong> {{ scanResult.total }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong class="text-success">Geçti:</strong> {{ scanResult.passed }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong class="text-danger">Eşik altı:</strong> {{ scanResult.failed }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Kayıt:</strong> {{ scanResult.savedEdges }} ({{ scanResult.durationMs }}ms)
          </b-col>
        </b-row>
        <b-table
          :items="scanResult.results"
          :fields="scanResultFields"
          small
          striped
          responsive
          :tbody-tr-class="rowClass"
        >
          <template #cell(passed)="data">
            <b-badge :variant="data.item.passed ? 'success' : 'secondary'">
              {{ data.item.passed ? 'GEÇTİ' : 'EŞİK ALTI' }}
            </b-badge>
          </template>
          <template #cell(scoreFinal)="data">
            <strong :class="data.item.scoreFinal >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(data.item.scoreFinal, 3) }}
            </strong>
          </template>
          <template #cell(netEvPerTrade)="data">
            <span :class="data.item.netEvPerTrade >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(data.item.netEvPerTrade, 3) }}%
            </span>
          </template>
          <template #cell(winRate)="data">
            {{ formatNum(data.item.winRate, 1) }}%
          </template>
          <template #cell(rejectReason)="data">
            <span class="font-small-3 text-muted">{{ data.item.rejectReason || '—' }}</span>
          </template>
        </b-table>
        <div class="text-right mt-2">
          <b-button
            variant="success"
            @click="closeScanAndRefresh"
          >
            <feather-icon icon="CheckIcon" /> Tamam, Library'i Güncelle
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BCardFooter, BTable, BPagination, BButton,
  BFormSelect, BFormInput, BFormCheckbox, BRow, BCol, BBadge, BSpinner,
  BModal,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'EdgesIndex',
  components: {
    BCard,
    BCardBody,
    BCardFooter,
    BTable,
    BPagination,
    BButton,
    BFormSelect,
    BFormInput,
    BFormCheckbox,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BModal,
  },
  data() {
    return {
      loading: false,
      rescanning: false,
      currentPage: 1,
      searchTimer: null,
      selectedIds: [],
      filter: {
        kind: undefined,
        market: undefined,
        symbol: '',
        timeframe: undefined,
        isActive: undefined,
        page: 1,
        limit: 50,
      },
      kindOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'NORMAL', text: 'Normal' },
        { value: 'QUICK_GAIN', text: 'Quick Gain' },
      ],
      marketOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      tfOptions: [
        { value: undefined, text: 'Tümü' },
        ...['1m', '5m', '15m', '30m', '1h', '4h', '1d'].map(v => ({ value: v, text: v })),
      ],
      activeOptions: [
        { value: undefined, text: 'Tümü' },
        { value: true, text: 'Aktif' },
        { value: false, text: 'Pasif' },
      ],
      fields: [
        { key: 'select', label: '', thClass: 'width-50' },
        { key: 'symbol', label: 'Coin/TF', thClass: 'width-150' },
        { key: 'indicator', label: 'İndikatör' },
        { key: 'kind', label: 'Tip', thClass: 'width-100 text-center' },
        { key: 'metrics', label: 'Metrikler' },
        { key: 'qg', label: 'QG TP/SL' },
        { key: 'score', label: 'Skor', thClass: 'width-100 text-center' },
        { key: 'isActive', label: 'Aktif', thClass: 'width-80 text-center' },
        { key: 'lastRescannedAt', label: 'Son Scan', thClass: 'width-150' },
        { key: 'actions', label: '', thClass: 'width-80' },
      ],
      scanResultFields: [
        { key: 'symbol', label: 'Coin' },
        { key: 'timeframe', label: 'TF', thClass: 'width-80' },
        { key: 'engineKey', label: 'Engine' },
        { key: 'passed', label: 'Durum', thClass: 'width-100' },
        { key: 'exits', label: 'Trade', thClass: 'width-80' },
        { key: 'winRate', label: 'WR', thClass: 'width-80' },
        { key: 'netEvPerTrade', label: 'Net EV', thClass: 'width-100' },
        { key: 'scoreFinal', label: 'Skor', thClass: 'width-100' },
        { key: 'rejectReason', label: 'Açıklama' },
      ],
      scanModal: { show: false },
      scanForm: {
        coinsRaw: 'BTCUSDT,ETHUSDT,SOLUSDT,XRPUSDT,DOGEUSDT',
        tfsRaw: '5m,15m,1h',
        indicatorIds: [],
        market: 'FUTURES',
        kind: 'NORMAL',
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
        minTrades: 10,
        minNetEv: 0,
        minWinRate: 0,
        candleLimit: 1500,
      },
    }
  },
  computed: {
    dataList() { return this.$store.getters['edges/dataList'] },
    metaData() { return this.$store.getters['edges/metaData'] },
    scanResult() { return this.$store.getters['edges/scanResult'] },
    scanLoading() { return this.$store.getters['edges/scanLoading'] },
    saveStatus() { return this.$store.getters['edges/dataSaveStatus'] },
    indicatorOptions() {
      const list = this.$store.getters['indicators/dataList']
      return (list || []).map(i => ({ value: i.id, text: `${i.name} (${i.engineKey})` }))
    },
    allSelected() {
      return this.dataList.length > 0 && this.dataList.every(e => this.selectedIds.includes(e.id))
    },
    gridSize() {
      const coins = this.parseCsv(this.scanForm.coinsRaw)
      const tfs = this.parseCsv(this.scanForm.tfsRaw)
      const inds = this.scanForm.indicatorIds || []
      return coins.length * tfs.length * inds.length
    },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    this.fetchList()
    // Indicators store doluysa kullan, değilse fetch et
    if (!this.indicatorOptions.length) {
      this.$store.dispatch('indicators/getDataList', {})
    }
  },
  methods: {
    parseCsv(str) {
      if (!str) return []
      return str.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
    },
    formatNum(value, decimals = 2) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(decimals)
    },
    formatDateTime(value) {
      if (!value) return '—'
      try {
        const d = new Date(value)
        if (isNaN(d.getTime())) return String(value)
        return d.toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) { return String(value) }
    },
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('edges/getDataList', { ...this.filter })
      this.loading = false
    },
    onPageChange(page) {
      this.currentPage = page
      this.filter.page = page
      this.fetchList()
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.filter.page = 1
        this.currentPage = 1
        this.fetchList()
      }, 400)
    },
    toggleSelected(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx >= 0) this.selectedIds.splice(idx, 1)
      else this.selectedIds.push(id)
    },
    toggleAllSelected(checked) {
      if (checked) {
        this.selectedIds = this.dataList.map(e => e.id)
      } else {
        this.selectedIds = []
      }
    },
    async onToggleActive(id, isActive) {
      await this.$store.dispatch('edges/toggleActive', { id, isActive })
      this.fetchList()
    },
    async onRemove(id) {
      this.$swal({
        title: 'Edge silinsin mi?',
        text: 'Soft delete: kayıt korunur ama listede görünmez.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await this.$store.dispatch('edges/removeEdge', id)
          this.fetchList()
        }
      })
    },
    async bulkAction(isActive) {
      await this.$store.dispatch('edges/bulkToggle', { ids: this.selectedIds, isActive })
      this.selectedIds = []
      this.fetchList()
    },
    async bulkRemove() {
      this.$swal({
        title: `${this.selectedIds.length} edge silinsin mi?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await this.$store.dispatch('edges/bulkRemove', this.selectedIds)
          this.selectedIds = []
          this.fetchList()
        }
      })
    },
    openScanModal() {
      this.scanModal.show = true
      this.$store.commit('edges/SET_SCAN_RESULT', null)
      this.ensureDefaultScanIndicators()
    },
    async ensureDefaultScanIndicators() {
      if ((this.scanForm.indicatorIds || []).length) return
      if (!this.indicatorOptions.length) {
        await this.$store.dispatch('indicators/getDataList', {})
      }
      const list = this.$store.getters['indicators/dataList'] || []
      this.scanForm.indicatorIds = list
        .filter(item => item && item.isActive !== false && item.id)
        .map(item => item.id)
    },
    async onRescanNow() {
      this.rescanning = true
      try {
        await this.$store.dispatch('edges/rescanNow', { kind: 'BOTH' })
      } finally {
        this.rescanning = false
      }
    },
    async runScan() {
      const coins = this.parseCsv(this.scanForm.coinsRaw)
      const timeframes = this.parseCsv(this.scanForm.tfsRaw).map(s => s.toLowerCase())
      const { indicatorIds } = this.scanForm
      if (!coins.length || !timeframes.length || !indicatorIds.length) {
        this.showToast({
          variant: 'danger', title: 'Eksik', icon: 'XIcon', text: 'Coin, TF ve indicator zorunlu.',
        })
        return
      }
      const body = {
        coins,
        timeframes,
        indicatorIds,
        market: this.scanForm.market,
        kind: this.scanForm.kind,
        candleLimit: this.scanForm.candleLimit,
        filters: {
          minTrades: this.scanForm.minTrades,
          minNetEv: this.scanForm.minNetEv,
          minWinRate: this.scanForm.minWinRate,
        },
      }
      if (this.scanForm.kind === 'QUICK_GAIN') {
        body.runtimeConfig = {
          tpPerc: this.scanForm.tpPerc,
          slPerc: this.scanForm.slPerc,
          leverage: this.scanForm.leverage,
          commissionPerSide: this.scanForm.commissionPerSide,
        }
      }
      await this.$store.dispatch('edges/runScan', body)
    },
    closeScanAndRefresh() {
      this.scanModal.show = false
      this.fetchList()
    },
    rowClass(item) {
      if (!item) return ''
      return item.passed ? 'table-success-soft' : ''
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.table-success-soft {
  background-color: rgba(40, 199, 111, 0.05) !important;
}
.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
}
</style>
